import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Clock,
  Play,
  Save,
  X,
  Plus,
  Trash2,
  CheckCircle,
  XCircle,
  AlertCircle,
  History,
  Settings,
  RefreshCw,
  Timer,
  ChevronDown,
  ChevronRight,
  Calendar,
  Package,
  Database,
  Megaphone,
  Truck,
  Mail,
  Activity,
  Search,
  DollarSign,
  Folder,
  Filter,
  PauseCircle
} from 'lucide-react'
import { schedulerApi, ScheduledTask, TaskExecutionLog, GroupedTasks, TaskCategory } from '@/lib/api'
import { cn } from '@/lib/utils'

// Day of week mapping
const DAYS_OF_WEEK = [
  { id: 'mon', label: 'L', fullLabel: 'Lunes' },
  { id: 'tue', label: 'M', fullLabel: 'Martes' },
  { id: 'wed', label: 'X', fullLabel: 'Miércoles' },
  { id: 'thu', label: 'J', fullLabel: 'Jueves' },
  { id: 'fri', label: 'V', fullLabel: 'Viernes' },
  { id: 'sat', label: 'S', fullLabel: 'Sábado' },
  { id: 'sun', label: 'D', fullLabel: 'Domingo' },
]

// Category icons mapping
const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  itsys: <Package className="h-4 w-4" />,
  factusol: <Database className="h-4 w-4" />,
  google_ads: <Megaphone className="h-4 w-4" />,
  shipping: <Truck className="h-4 w-4" />,
  email: <Mail className="h-4 w-4" />,
  monitoring: <Activity className="h-4 w-4" />,
  seo: <Search className="h-4 w-4" />,
  pricing: <DollarSign className="h-4 w-4" />,
  other: <Folder className="h-4 w-4" />,
}

// Category colors mapping (Tailwind classes)
const CATEGORY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  itsys: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  factusol: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  google_ads: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
  shipping: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  email: { bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200' },
  monitoring: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  seo: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  pricing: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  other: { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200' },
}

interface EditFormState {
  enabled: boolean
  execution_times: string[]
  day_of_week: string
  name: string
  description: string
}

export default function SchedulerPage() {
  const queryClient = useQueryClient()
  const [editingTask, setEditingTask] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<EditFormState>({
    enabled: true,
    execution_times: [],
    day_of_week: '*',
    name: '',
    description: ''
  })
  const [activeTab, setActiveTab] = useState<'tasks' | 'history'>('tasks')
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set())
  const [filterStatus, setFilterStatus] = useState<'all' | 'errors' | 'disabled'>('all')

  // Fetch grouped tasks
  const { data: groupedData, isLoading: tasksLoading } = useQuery({
    queryKey: ['scheduler-grouped'],
    queryFn: schedulerApi.getGroupedTasks,
    refetchInterval: 30000
  })

  // Fetch summary
  const { data: summaryData } = useQuery({
    queryKey: ['scheduler-summary'],
    queryFn: schedulerApi.getSummary,
    refetchInterval: 30000
  })

  // Fetch history
  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['scheduler-history'],
    queryFn: () => schedulerApi.getHistory(100),
    enabled: activeTab === 'history'
  })

  // Update task mutation
  const updateTaskMutation = useMutation({
    mutationFn: ({ taskId, data }: { taskId: string; data: { enabled: boolean; execution_times: string[]; day_of_week?: string; name?: string; description?: string } }) =>
      schedulerApi.updateTask(taskId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduler-grouped'] })
      queryClient.invalidateQueries({ queryKey: ['scheduler-summary'] })
      setEditingTask(null)
    }
  })

  // Run task mutation
  const runTaskMutation = useMutation({
    mutationFn: (taskId: string) => schedulerApi.runTask(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduler-grouped'] })
      queryClient.invalidateQueries({ queryKey: ['scheduler-summary'] })
      queryClient.invalidateQueries({ queryKey: ['scheduler-history'] })
    }
  })

  const grouped = groupedData?.grouped || {}
  const summary = summaryData || { total: 0, enabled: 0, disabled: 0, success: 0, error: 0, never_run: 0, by_category: {}, errors_by_category: {} }
  const history = historyData?.history || []

  const startEditing = (task: ScheduledTask) => {
    setEditingTask(task.task_id)
    setEditForm({
      enabled: task.enabled,
      execution_times: [...task.execution_times],
      day_of_week: task.day_of_week || '*',
      name: task.name,
      description: task.description
    })
  }

  const cancelEditing = () => {
    setEditingTask(null)
    setEditForm({ enabled: true, execution_times: [], day_of_week: '*', name: '', description: '' })
  }

  const addTime = () => {
    setEditForm(prev => ({
      ...prev,
      execution_times: [...prev.execution_times, '09:00']
    }))
  }

  const removeTime = (index: number) => {
    setEditForm(prev => ({
      ...prev,
      execution_times: prev.execution_times.filter((_, i) => i !== index)
    }))
  }

  const updateTime = (index: number, value: string) => {
    setEditForm(prev => ({
      ...prev,
      execution_times: prev.execution_times.map((t, i) => i === index ? value : t)
    }))
  }

  const toggleDay = (dayId: string) => {
    setEditForm(prev => {
      if (prev.day_of_week === '*') {
        // If all days selected, switch to just this day
        return { ...prev, day_of_week: dayId }
      }

      const currentDays = prev.day_of_week.split(',').filter(d => d)
      const dayIndex = currentDays.indexOf(dayId)

      if (dayIndex >= 0) {
        // Remove day
        const newDays = currentDays.filter(d => d !== dayId)
        return { ...prev, day_of_week: newDays.length === 0 ? '*' : newDays.join(',') }
      } else {
        // Add day
        const newDays = [...currentDays, dayId]
        // If all days selected, use '*'
        if (newDays.length === 7) {
          return { ...prev, day_of_week: '*' }
        }
        return { ...prev, day_of_week: newDays.join(',') }
      }
    })
  }

  const selectAllDays = () => {
    setEditForm(prev => ({ ...prev, day_of_week: '*' }))
  }

  const isDaySelected = (dayId: string) => {
    if (editForm.day_of_week === '*') return true
    return editForm.day_of_week.split(',').includes(dayId)
  }

  const saveTask = (taskId: string) => {
    updateTaskMutation.mutate({
      taskId,
      data: {
        enabled: editForm.enabled,
        execution_times: editForm.execution_times,
        day_of_week: editForm.day_of_week,
        name: editForm.name,
        description: editForm.description
      }
    })
  }

  const toggleCategory = (categoryId: string) => {
    setCollapsedCategories(prev => {
      const newSet = new Set(prev)
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId)
      } else {
        newSet.add(categoryId)
      }
      return newSet
    })
  }

  const formatDateTime = (isoString: string | null) => {
    if (!isoString) return '-'
    const date = new Date(isoString)
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatRelativeTime = (isoString: string | null) => {
    if (!isoString) return '-'
    const date = new Date(isoString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return 'Ahora'
    if (diffMins < 60) return `Hace ${diffMins}min`
    if (diffHours < 24) return `Hace ${diffHours}h`
    return `Hace ${diffDays}d`
  }

  const formatFutureTime = (isoString: string | null) => {
    if (!isoString) return '-'
    const date = new Date(isoString)
    const now = new Date()
    const diffMs = date.getTime() - now.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 0) return 'Ahora'
    if (diffMins < 1) return '<1min'
    if (diffMins < 60) return `${diffMins}min`
    if (diffHours < 24) {
      const remainingMins = diffMins % 60
      return remainingMins > 0 ? `${diffHours}h ${remainingMins}min` : `${diffHours}h`
    }
    // Show date for times more than 24h away
    return date.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  }

  const formatDayOfWeek = (dow: string | undefined) => {
    if (!dow || dow === '*') return 'Todos los días'
    const days = dow.split(',')
    if (days.length === 5 && !days.includes('sat') && !days.includes('sun')) {
      return 'L-V'
    }
    return days.map(d => {
      const day = DAYS_OF_WEEK.find(dw => dw.id === d)
      return day?.label || d
    }).join(', ')
  }

  const getStatusIcon = (status: string | null) => {
    if (status === 'success') return <CheckCircle className="h-4 w-4 text-green-500" />
    if (status === 'error') return <XCircle className="h-4 w-4 text-red-500" />
    return <AlertCircle className="h-4 w-4 text-slate-400" />
  }

  const getStatusBadge = (status: string | null) => {
    if (status === 'success') {
      return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">OK</span>
    }
    if (status === 'error') {
      return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">Error</span>
    }
    return <span className="px-2 py-1 text-xs rounded-full bg-slate-100 text-slate-600">-</span>
  }

  const filterTasks = (tasks: ScheduledTask[]) => {
    if (filterStatus === 'all') return tasks
    if (filterStatus === 'errors') return tasks.filter(t => t.last_status === 'error')
    if (filterStatus === 'disabled') return tasks.filter(t => !t.enabled)
    return tasks
  }

  const renderSummaryCards = () => (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <div className="bg-white rounded-lg border p-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <CheckCircle className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{summary.success}</p>
            <p className="text-sm text-slate-500">Correctas</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border p-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-100 rounded-lg">
            <XCircle className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{summary.error}</p>
            <p className="text-sm text-slate-500">Con errores</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border p-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-slate-100 rounded-lg">
            <PauseCircle className="h-5 w-5 text-slate-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{summary.disabled}</p>
            <p className="text-sm text-slate-500">Deshabilitadas</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border p-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Clock className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{summary.total}</p>
            <p className="text-sm text-slate-500">Total tareas</p>
          </div>
        </div>
      </div>
    </div>
  )

  const renderTaskCard = (task: ScheduledTask) => {
    const isEditing = editingTask === task.task_id

    return (
      <div
        key={task.task_id}
        className={cn(
          'bg-white rounded-lg border p-4 shadow-sm transition-all',
          !task.enabled && 'opacity-60',
          task.last_status === 'error' && 'border-red-200 bg-red-50/30'
        )}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-900 truncate">{task.name}</h3>
              {task.last_status === 'error' && (
                <span className="px-1.5 py-0.5 text-xs rounded bg-red-100 text-red-700">Error</span>
              )}
            </div>
            <p className="text-xs text-slate-500 truncate">{task.description}</p>
          </div>
          <div className="flex items-center gap-1 ml-2">
            {!isEditing ? (
              <>
                <button
                  onClick={() => runTaskMutation.mutate(task.task_id)}
                  disabled={runTaskMutation.isPending}
                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  title="Ejecutar ahora"
                >
                  <Play className="h-4 w-4" />
                </button>
                <button
                  onClick={() => startEditing(task)}
                  className="p-1.5 text-slate-600 hover:bg-slate-100 rounded transition-colors"
                  title="Configurar"
                >
                  <Settings className="h-4 w-4" />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => saveTask(task.task_id)}
                  disabled={updateTaskMutation.isPending}
                  className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                  title="Guardar"
                >
                  <Save className="h-4 w-4" />
                </button>
                <button
                  onClick={cancelEditing}
                  className="p-1.5 text-slate-600 hover:bg-slate-100 rounded transition-colors"
                  title="Cancelar"
                >
                  <X className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>

        {isEditing ? (
          <div className="space-y-4">
            {/* Name input */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Nombre</label>
              <input
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-300"
                placeholder="Nombre de la tarea"
              />
            </div>

            {/* Description input */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Descripción</label>
              <textarea
                value={editForm.description}
                onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none"
                rows={2}
                placeholder="Descripción de la tarea"
              />
            </div>

            {/* Enabled toggle */}
            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={editForm.enabled}
                  onChange={(e) => setEditForm(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
              <span className="text-sm text-gray-700">Habilitada</span>
            </div>

            {/* Day of week selector */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Días de ejecución</label>
                <button
                  onClick={selectAllDays}
                  className="text-xs text-blue-600 hover:text-blue-700"
                >
                  Todos
                </button>
              </div>
              <div className="flex gap-1">
                {DAYS_OF_WEEK.map(day => (
                  <button
                    key={day.id}
                    onClick={() => toggleDay(day.id)}
                    title={day.fullLabel}
                    className={cn(
                      'w-8 h-8 rounded-full text-xs font-medium transition-colors',
                      isDaySelected(day.id)
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    )}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Execution times */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Horarios</label>
                <button
                  onClick={addTime}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                >
                  <Plus className="h-3 w-3" />
                  Añadir
                </button>
              </div>

              {editForm.execution_times.length === 0 ? (
                task.interval_minutes ? (
                  <div className="flex items-center gap-2 text-xs text-blue-600 bg-blue-50 px-3 py-2 rounded">
                    <Timer className="h-3 w-3" />
                    <span>Cada {task.interval_minutes} min (automático)</span>
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 italic">Sin horarios (solo manual)</p>
                )
              ) : (
                <div className="flex flex-wrap gap-2">
                  {editForm.execution_times.map((time, index) => (
                    <div key={index} className="flex items-center gap-1 bg-slate-100 rounded px-2 py-1">
                      <input
                        type="time"
                        value={time}
                        onChange={(e) => updateTime(index, e.target.value)}
                        className="bg-transparent text-sm text-gray-900 focus:outline-none w-20"
                      />
                      <button
                        onClick={() => removeTime(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Schedule info row */}
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {task.enabled ? (
                <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700">Activa</span>
              ) : (
                <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Pausada</span>
              )}

              {task.day_of_week && task.day_of_week !== '*' && (
                <span className="flex items-center gap-1 text-slate-600">
                  <Calendar className="h-3 w-3" />
                  {formatDayOfWeek(task.day_of_week)}
                </span>
              )}

              {task.execution_times.length > 0 ? (
                <span className="flex items-center gap-1 text-slate-600">
                  <Clock className="h-3 w-3" />
                  {task.execution_times.length > 3
                    ? `${task.execution_times.length} horarios`
                    : task.execution_times.join(', ')}
                </span>
              ) : task.interval_minutes ? (
                <span className="flex items-center gap-1 text-blue-600">
                  <Timer className="h-3 w-3" />
                  c/{task.interval_minutes}min
                </span>
              ) : null}
            </div>

            {/* Last execution */}
            <div className="flex items-center justify-between pt-2 border-t text-xs">
              <div className="flex items-center gap-2">
                {getStatusIcon(task.last_status)}
                <span className="text-slate-600">
                  {formatRelativeTime(task.last_run)}
                </span>
              </div>
              {task.next_run && (
                <span className="text-slate-500">
                  Próx: {formatFutureTime(task.next_run)}
                </span>
              )}
            </div>

            {task.last_message && task.last_status === 'error' && (
              <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-700 truncate" title={task.last_message}>
                {task.last_message}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  const renderCategoryGroup = (categoryId: string, data: { info: TaskCategory; tasks: ScheduledTask[] }) => {
    const isCollapsed = collapsedCategories.has(categoryId)
    const filteredTasks = filterTasks(data.tasks)
    const errorCount = data.tasks.filter(t => t.last_status === 'error').length
    const colors = CATEGORY_COLORS[categoryId] || CATEGORY_COLORS.other

    if (filteredTasks.length === 0 && filterStatus !== 'all') return null

    return (
      <div key={categoryId} className="mb-6">
        <button
          onClick={() => toggleCategory(categoryId)}
          className={cn(
            'w-full flex items-center justify-between p-3 rounded-lg border transition-colors',
            colors.bg, colors.border, 'hover:opacity-90'
          )}
        >
          <div className="flex items-center gap-3">
            <div className={cn('p-1.5 rounded', colors.text)}>
              {CATEGORY_ICONS[categoryId] || CATEGORY_ICONS.other}
            </div>
            <div className="text-left">
              <h2 className={cn('font-semibold', colors.text)}>{data.info.name}</h2>
              <p className="text-xs text-slate-500">{data.tasks.length} tareas</p>
            </div>
            {errorCount > 0 && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">
                {errorCount} error{errorCount > 1 ? 'es' : ''}
              </span>
            )}
          </div>
          {isCollapsed ? (
            <ChevronRight className="h-5 w-5 text-slate-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-slate-400" />
          )}
        </button>

        {!isCollapsed && (
          <div className="mt-3 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {filteredTasks.map(renderTaskCard)}
          </div>
        )}
      </div>
    )
  }

  const renderHistoryTable = () => {
    if (historyLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      )
    }

    if (history.length === 0) {
      return (
        <div className="text-center py-12 text-slate-500">
          No hay historial de ejecuciones
        </div>
      )
    }

    return (
      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Tarea</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Inicio</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Duración</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Estado</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Mensaje</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {history.map((log) => (
              <tr key={log.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                  {log.task_name}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600">
                  {formatDateTime(log.started_at)}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600">
                  {log.duration_seconds != null
                    ? `${log.duration_seconds.toFixed(1)}s`
                    : '-'
                  }
                </td>
                <td className="px-4 py-3">
                  {getStatusBadge(log.status)}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate" title={log.message || ''}>
                  {log.message || '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tareas Programadas</h1>
          <p className="text-slate-500">Configura la ejecución automática de tareas</p>
        </div>
        <div className="text-sm text-slate-500">
          Zona horaria: Europe/Madrid
        </div>
      </div>

      {/* Summary Cards */}
      {activeTab === 'tasks' && renderSummaryCards()}

      {/* Tabs */}
      <div className="flex items-center justify-between border-b">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('tasks')}
            className={cn(
              'pb-3 text-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === 'tasks'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-gray-700'
            )}
          >
            <Settings className="h-4 w-4 inline mr-2" />
            Configuración
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={cn(
              'pb-3 text-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === 'history'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-gray-700'
            )}
          >
            <History className="h-4 w-4 inline mr-2" />
            Historial
          </button>
        </nav>

        {/* Filter */}
        {activeTab === 'tasks' && (
          <div className="flex items-center gap-2 pb-3">
            <Filter className="h-4 w-4 text-slate-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as 'all' | 'errors' | 'disabled')}
              className="text-sm border-0 bg-transparent text-slate-600 focus:ring-0 cursor-pointer"
            >
              <option value="all">Todas</option>
              <option value="errors">Solo errores</option>
              <option value="disabled">Solo deshabilitadas</option>
            </select>
          </div>
        )}
      </div>

      {/* Content */}
      {activeTab === 'tasks' && (
        <>
          {tasksLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : (
            <div>
              {Object.entries(grouped).map(([categoryId, data]) =>
                renderCategoryGroup(categoryId, data)
              )}
            </div>
          )}
        </>
      )}

      {activeTab === 'history' && renderHistoryTable()}
    </div>
  )
}
